"use client";

import { compareDesc, isBefore, parseISO } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";

import { ArrowUp } from "lucide-react";
import PostPreviewTile from "../PostPreviewTile";
import { WPRecentPost } from "@/types";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";

const lastDateToCheck = parseISO("2024-11-17");
const PAGE_SIZE = 5;

interface RecentPostPreviewsProps {
  recentPosts: WPRecentPost[];
  authorName?: string;
  main?: boolean;
}

export const RecentPostsPreviews = ({
  recentPosts,
  authorName,
  main,
}: RecentPostPreviewsProps) => {
  const pathName = usePathname();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const sortedRecentPosts = useMemo(
    () =>
      [...recentPosts].sort((a, b) => {
        const dateA = parseISO(a.node.actual_published_date || a.node.date);
        const dateB = parseISO(b.node.actual_published_date || b.node.date);
        if (
          isBefore(dateA, lastDateToCheck) &&
          isBefore(dateB, lastDateToCheck)
        )
          return compareDesc(dateA, dateB);
        if (isBefore(dateA, lastDateToCheck)) return 1;
        if (isBefore(dateB, lastDateToCheck)) return -1;
        return compareDesc(dateA, dateB);
      }),
    [recentPosts],
  );

  const currentSlug = useMemo(() => {
    const match = pathName.match(/\/post\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : undefined;
  }, [pathName]);

  const filteredPosts = useMemo(
    () =>
      main
        ? sortedRecentPosts
        : sortedRecentPosts
            .filter((p) => p.node.slug !== currentSlug)
            .slice(0, 3),
    [sortedRecentPosts, main, currentSlug],
  );

  const visiblePosts = main
    ? filteredPosts.slice(0, visibleCount)
    : filteredPosts;
  const hasMore = main && visibleCount < filteredPosts.length;

  // Infinite scroll — only active on the main blog listing
  useEffect(() => {
    if (!main || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore) {
          setVisibleCount((prev) =>
            Math.min(prev + PAGE_SIZE, filteredPosts.length),
          );
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [main, hasMore, filteredPosts.length]);

  if (!recentPosts.length)
    return <div className="loading">Loading recent posts...</div>;

  return (
    <div>
      <div
        className={cn(
          main ? "flex flex-col mt-14" : "grid items-stretch md:grid-cols-3",
          "md:gap-6 md:pt-8 pt-2 gap-2",
        )}
      >
        {visiblePosts.map((post: WPRecentPost) => (
          <PostPreviewTile post={post} main={main} key={post.node.id} />
        ))}
      </div>

      {/* sentinel — observed to trigger next page */}
      {main && <div ref={sentinelRef} className="h-px mt-16" />}

      {main && !hasMore && (
        <a
          href="#blog-top"
          className="flex flex-col items-center gap-2 mt-12 mb-8 text-sm font-Cabin text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 transition-colors group"
        >
          <ArrowUp className="h-4 w-4 group-hover:-translate-y-1 transition-transform duration-200" />
          Back to top
        </a>
      )}
    </div>
  );
};

export default RecentPostsPreviews;
